import { GUnit } from 'interfaces/IGlobalCatalogue';
import { IInputZone, ILink, ZoneLinkType } from 'interfaces/IInput';
import { IPaddock } from 'interfaces/IPaddock';
import { IZone } from 'interfaces/IZone';
import { resolveAreaUnit } from 'utils/inputDraftPatches';

/**
 * Builds the input's links the way the mobile app's `createInputZone` does:
 * the zone's own links (which carry the paddock) with a self-link to the zone
 * appended last.
 *
 * The one addition is a safeguard mobile does not need — if the zone carries
 * no paddock link, one is prepended from the paddock. Without it a zone with
 * empty links would produce a plan that never files under its paddock in the
 * tree, since groupInputsByFarmHierarchy matches on the Paddock link.
 */
const buildZoneLinks = (zone: IZone, paddock: IPaddock): ILink[] => {
  const existingLinks: ILink[] = zone.links.map((link) => ({
    linkType: link.linkType,
    linkId: link.linkId,
    snapLinkName: link.snapLinkName,
  }));

  const hasPaddockLink = existingLinks.some(
    (link) => link.linkType === ZoneLinkType.Paddock
  );

  const links = hasPaddockLink
    ? existingLinks
    : [
        {
          linkType: ZoneLinkType.Paddock,
          linkId: paddock.paddockId,
          snapLinkName: paddock.paddockName,
        },
        ...existingLinks,
      ];

  return [
    ...links,
    {
      linkType: ZoneLinkType.Zone,
      linkId: zone.id,
      snapLinkName: zone.zoneName,
    },
  ];
};

/**
 * Maps a Zone document onto the zone an Input document embeds, mirroring the
 * mobile app's `createInputZone`. The Input's zone is a projection of the Zone
 * record, so every field is taken from the zone rather than reconstructed from
 * the paddock — which is what previously left `zoneName` and `geoData` wrong.
 *
 * The area unit is the deliberate exception: it is passed through
 * `resolveAreaUnit` so an absent or empty-GUID globalId inherited from the
 * Zone is repaired, which mobile does not do.
 */
export const buildInputZone = (
  zone: IZone,
  paddock: IPaddock,
  gUnits: GUnit[]
): IInputZone => ({
  zoneName: zone.zoneName ?? '',
  zoneArea: Number(zone.zoneArea || 0),
  areaUnit: resolveAreaUnit(zone.areaUnit, gUnits),
  geoData: zone.geoData ?? null,
  links: buildZoneLinks(zone, paddock),
});

export default buildInputZone;
